import { useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type {
  IFilter,
  IFilterProps,
  IFilterValue,
} from "@components/molecules/filter/types";
import {
  filterToObjectMap,
  flattenJsonToQueryParam,
  getFilterParamName,
  queryParamsToNestedJson,
} from "@components/molecules/filter/helper";
import { decodeURIComponentSafe } from "@utils";

const FILTER_TIME = 800;

/**
 * user selected some filters.
 * we set the timer.
 * if the value of the filters has changed before finishing the timeout:
 *   YES => refresh the timeout
 *   NO => we set the new timer
 * in the callback of the timeout, we have to clear the timer and set the debounced value.
 *   and if the onChange is not undefined call it.
 * finally, return value and onChange to handle the filter value.
 *    and debouncedValue to use in a query.
 *
 *
 * @param options
 * @param index
 * @param onChange a function to call when the debounced value has changed.
 */
export const useFilterParam = (
  options: IFilter[] | undefined,
  index?: string | number,
  onChange?: (value: URLSearchParams) => void,
) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { search, state } = useMemo(() => location, [location]);
  const filterParamName = useMemo(() => getFilterParamName(index), [index]);
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const filterParam = useMemo(
    () => params.get(filterParamName),
    [filterParamName, params],
  );

  // get the search param value from the URL
  const initialValue = useMemo(
    () =>
      filterParam === null
        ? []
        : (queryParamsToNestedJson(
            decodeURIComponentSafe(filterParam),
          ) as unknown as IFilterValue[]),
    [filterParam],
  );

  // state for store the input value and debounced value
  const [value, setValue] = useState<IFilterValue[]>(initialValue);
  const [debouncedValue, setDebouncedValue] =
    useState<IFilterValue[]>(initialValue);

  // use ref to store the timer
  // eslint-disable-next-line no-undef
  const timer = useRef<NodeJS.Timeout | null>(null);

  // declare set and delete param to/from the URL
  const setParam = useCallback(
    (value: IFilterValue[]) => {
      params.set(
        filterParamName,
        flattenJsonToQueryParam(JSON.stringify(value, null, 0)),
      );
      navigate({ search: params.toString() }, { state, replace: true });
    },
    [filterParamName, navigate, params, state],
  );

  // timeout callback function
  const setKeyword = useCallback(
    (value: IFilterValue[]) => {
      // set the url param
      setParam(value);

      // set the debounced value
      setDebouncedValue(value);
      if (onChange) onChange(params);

      // clear the timer
      if (timer.current !== null) {
        clearTimeout(timer.current);
      }
      timer.current = null;
    },
    [onChange, params, setParam],
  );

  // input change handler
  const changeHandler = useCallback(
    (filterValue: IFilterValue[]) => {
      setValue(filterValue);

      if (timer.current === null) {
        // set the timeout
        timer.current = setTimeout(() => setKeyword(filterValue), FILTER_TIME);
      } else {
        // refresh the timer
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setKeyword(filterValue), FILTER_TIME);
      }
    },
    [setKeyword],
  );

  const resetFilter = useCallback(() => {
    setParam([]);
    setValue([]);
  }, [setParam]);

  useEffect(() => {
    if (filterParam === null) {
      setDebouncedValue([]);
      setValue([]);
    }
  }, [filterParam]);

  const filterProps: IFilterProps | undefined = useMemo(
    () =>
      options
        ? {
            onFilterSelect: changeHandler,
            index,
            options,
            value,
          }
        : undefined,
    [changeHandler, index, options, value],
  );

  return useMemo(
    () => ({
      filterProps,
      filterValues: options
        ? filterToObjectMap<typeof options, typeof value>(
            options,
            debouncedValue,
          )
        : {},
      resetFilter,
    }),
    [debouncedValue, filterProps, options, resetFilter],
  );
};
